// Production-optimized logging system
const isProduction = import.meta.env.PROD;

// Log levels
enum LogLevel {
  ERROR = 0,
  WARN = 1,
  INFO = 2,
  DEBUG = 3
}

// Production log level (only errors and warnings)
const PRODUCTION_LOG_LEVEL = LogLevel.WARN;
const DEVELOPMENT_LOG_LEVEL = LogLevel.DEBUG;

const currentLogLevel = isProduction ? PRODUCTION_LOG_LEVEL : DEVELOPMENT_LOG_LEVEL;

// Store original console methods to prevent recursion
const originalConsole = {
  log: console.log,
  info: console.info,
  warn: console.warn,
  error: console.error,
  debug: console.debug
};

// Optimized logger
export const logger = {
  error: (message: string, ...args: unknown[]) => {
    if (currentLogLevel >= LogLevel.ERROR) {
      originalConsole.error(`[ERROR] ${message}`, ...args);
    }
  },
  
  warn: (message: string, ...args: unknown[]) => {
    if (currentLogLevel >= LogLevel.WARN) {
      originalConsole.warn(`[WARN] ${message}`, ...args);
    }
  },
  
  info: (message: string, ...args: unknown[]) => {
    if (currentLogLevel >= LogLevel.INFO) {
      originalConsole.info(`[INFO] ${message}`, ...args);
    }
  },
  
  debug: (message: string, ...args: unknown[]) => {
    if (currentLogLevel >= LogLevel.DEBUG) {
      originalConsole.log(`[DEBUG] ${message}`, ...args);
    }
  },
  
  // Specific loggers for different concerns
  performance: (component: string, duration: number, ...args: unknown[]) => {
    if (duration > 100) {
      if (currentLogLevel >= LogLevel.INFO) {
        originalConsole.info(`[PERF] ${component} rendered in ${duration}ms`, ...args);
      }
    }
  },
  
  api: (endpoint: string, duration: number, status?: number) => {
    if (duration > 1000 || (status && status >= 400)) {
      if (currentLogLevel >= LogLevel.WARN) {
        originalConsole.warn(`[API] ${endpoint} (${duration}ms, status: ${status})`);
      }
    } else if (!isProduction && duration > 500) {
      if (currentLogLevel >= LogLevel.DEBUG) {
        originalConsole.log(`[API] ${endpoint} (${duration}ms)`);
      }
    }
  },
  
  navigation: (from: string, to: string) => {
    if (!isProduction && currentLogLevel >= LogLevel.DEBUG) {
      originalConsole.log(`[NAV] ${from} → ${to}`);
    }
  },
  
  auth: (event: string, ...args: unknown[]) => {
    // Auth events are always important
    if (currentLogLevel >= LogLevel.INFO) {
      originalConsole.info(`[AUTH] ${event}`, ...args);
    }
  }
};

// Simple console override for production (without recursion)
if (isProduction) {
  // Override with no-op in production
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  
  // Provide escape hatch for critical debugging
  (window as any).__enableDebugLogs = () => {
    console.log = originalConsole.log;
    console.info = originalConsole.info;
    console.debug = originalConsole.debug;
    originalConsole.info('Debug logging enabled in production');
  };
}